import { useState, type ReactNode } from 'react';

import { Card, CardBody, cn, Input, Logo, type InputProps } from '@platform/ui';
import { ObjectLabsMark } from '@platform/ui/object-labs-mark';

/**
 * The chrome the signed-out and gated screens share (M2.8, ADR-026, FR-UI-03).
 *
 * `/login` and `/mfa/enroll` render outside the `AppShell`: there is no nav to show
 * someone who cannot yet reach any of it, and a shell around a sign-in form implies a
 * console they are already inside. What they get instead is the platform logo, one card,
 * and the Object Labs mark at the foot - the only place in the platform that mark
 * appears, because this is the only app that is Object Labs' own (ADR-026).
 *
 * The super admin console always takes the default HeyAllergy theme and never tenant
 * branding (FR-UI-03), so `Logo` is asked for the platform variant explicitly.
 */

export interface AuthCardProps {
  title: string;
  /** One sentence under the title. */
  lede?: ReactNode;
  children: ReactNode;
  /** Wider than the default, for the enrolment screen's QR and backup codes. */
  width?: 'narrow' | 'wide';
}

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <header className="flex h-14 shrink-0 items-center border-b border-neutral-200 bg-surface px-6 max-[480px]:px-4">
        <Logo variant="platform" height={26} />
      </header>

      <main className="flex flex-1 items-start justify-center px-6 py-10 max-[480px]:px-4 max-[480px]:py-6">
        {children}
      </main>

      <footer className="flex flex-wrap items-center justify-center gap-2 border-t border-neutral-200 px-6 py-4 text-xs text-neutral-400-text">
        Operated by
        <ObjectLabsMark height={14} />
      </footer>
    </div>
  );
}

export function AuthCard({ title, lede, children, width = 'narrow' }: AuthCardProps) {
  return (
    <div className={cn('w-full', width === 'wide' ? 'max-w-[640px]' : 'max-w-[420px]')}>
      <Card>
        <CardBody>
          {/* The card *is* the page here - there is no title above it - so this is the h1. */}
          <h1 className="mb-1 text-xl font-bold text-ink">{title}</h1>
          {lede === undefined ? null : (
            <p className="mb-5 text-base leading-[1.5] text-neutral-500">{lede}</p>
          )}
          {children}
        </CardBody>
      </Card>
    </div>
  );
}

export type PasswordInputProps = Omit<InputProps, 'type'> & {
  autoComplete: 'current-password' | 'new-password';
};

/**
 * A password field with a show/hide toggle.
 *
 * The toggle is a real button whose accessible name changes with the state, and it sits
 * inside the field's box so the 44px target does not squeeze the input at 390px. Same
 * component as the patient app's, kept local because it is nine lines and the shared
 * package should not grow a second input primitive for one screen.
 */
export function PasswordInput({ className, ...rest }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input {...rest} type={visible ? 'text' : 'password'} className={cn('pr-12', className)} />
      <button
        type="button"
        onClick={() => {
          setVisible((current) => !current);
        }}
        aria-pressed={visible}
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute top-1/2 right-1 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-input text-neutral-500 hover:text-ink"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
          <circle cx="12" cy="12" r="3" />
          {visible ? <path d="m3 3 18 18" /> : null}
        </svg>
      </button>
    </div>
  );
}
